import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ChevronDown, Sparkles } from "lucide-react";

/* Layered parallax images give a real 3D depth feeling */
const LAYERS = [
  // Far back: misty mountains
  {
    src: "https://images.unsplash.com/photo-1568489711036-9c94a7d5aea6?auto=format&fit=crop&w=2000&q=85",
    z: -300, scale: 1.3, blur: 2, opacity: 0.85,
  },
  // Mid: forest canopy
  {
    src: "https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&w=2000&q=85",
    z: -150, scale: 1.15, blur: 0.5, opacity: 0.85,
  },
  // Front foreground: ferns/leaves silhouette
  {
    src: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=2000&q=85",
    z: 0, scale: 1.05, blur: 0, opacity: 1,
  },
];

function Fireflies({ count = 22 }) {
  return (
    <div className="absolute inset-0 pointer-events-none">
      {Array.from({ length: count }).map((_, i) => {
        const left = Math.random() * 100;
        const top = 30 + Math.random() * 60;
        const delay = Math.random() * 6;
        const dur = 5 + Math.random() * 6;
        return (
          <span
            key={i}
            className="firefly"
            style={{
              left: `${left}%`,
              top: `${top}%`,
              animationDelay: `${delay}s`,
              animationDuration: `${dur}s`,
            }}
          />
        );
      })}
    </div>
  );
}

function Leaves({ count = 8 }) {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {Array.from({ length: count }).map((_, i) => {
        const left = Math.random() * 100;
        const dur = 10 + Math.random() * 12;
        const delay = Math.random() * 8;
        return (
          <span
            key={i}
            className="leaf"
            style={{
              left: `${left}%`,
              animationDuration: `${dur}s`,
              animationDelay: `${delay}s`,
            }}
          />
        );
      })}
    </div>
  );
}

export default function Hero({ content = {} }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  const LAYERS_DYNAMIC = [
    { src: content.hero_image_far  || LAYERS[0].src, z: -300, scale: 1.3,  blur: 2,   opacity: 0.85 },
    { src: content.hero_image_mid  || LAYERS[1].src, z: -150, scale: 1.15, blur: 0.5, opacity: 0.85 },
    { src: content.hero_image_front|| LAYERS[2].src, z: 0,    scale: 1.05, blur: 0,   opacity: 1 },
  ];

  // Parallax depth per layer
  const yFar = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const yMid = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const yFront = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  // Camera dolly in (zoom on scroll like entering portal)
  const camScale = useTransform(scrollYProgress, [0, 1], [1, 1.45]);
  const camOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const titleY = useTransform(scrollYProgress, [0, 0.6], [0, -120]);

  // Mouse 3D tilt for portal
  const mx = useSpring(0, { stiffness: 80, damping: 18 });
  const my = useSpring(0, { stiffness: 80, damping: 18 });
  const tiltY = useTransform(mx, [-1, 1], [-12, 12]);
  const tiltX = useTransform(my, [-1, 1], [10, -10]);

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - rect.left) / rect.width) * 2 - 1);
    my.set(((e.clientY - rect.top) / rect.height) * 2 - 1);
  };

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative min-h-[100vh] flex items-center overflow-hidden bg-[#0B1A0F]"
      style={{ perspective: "1400px", perspectiveOrigin: "50% 60%" }}
    >
      {/* 3D parallax layers */}
      <motion.div className="absolute inset-0" style={{ scale: camScale, opacity: camOpacity }}>
        {LAYERS_DYNAMIC.map((L, i) => (
          <motion.div
            key={i}
            className="absolute inset-0"
            style={{
              y: i === 0 ? yFar : i === 1 ? yMid : yFront,
              transform: `translateZ(${L.z}px) scale(${L.scale})`,
              filter: `blur(${L.blur}px)`,
              opacity: L.opacity,
            }}
          >
            <img
              src={L.src}
              alt=""
              className="w-full h-full object-cover"
              draggable="false"
            />
          </motion.div>
        ))}
        {/* Color grade overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1A0F]/55 via-[#15301E]/35 to-[#0B1A0F]/95" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_45%,transparent,rgba(11,26,15,0.85))]" />
        <div className="fog-layer" />
        <div className="absolute inset-0 paper-grain" />
      </motion.div>

      {/* Floating glow orbs */}
      <div className="orb w-[520px] h-[520px] bg-[#B8E07A] top-[-160px] right-[-180px] animate-float-y" />
      <div className="orb w-[380px] h-[380px] bg-[#F1C36B] bottom-[-120px] left-[-120px]" style={{ animationDelay: "2s" }} />

      <Fireflies />
      <Leaves />

      {/* Foreground content */}
      <motion.div
        className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full pt-28 pb-24 grid lg:grid-cols-12 gap-10 items-center"
        style={{ y: titleY }}
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7 text-[#F2EFE6]"
        >
          <span
            data-testid="hero-overline"
            className="inline-flex items-center gap-2 text-xs sm:text-sm uppercase tracking-[0.3em] font-medium text-[#F1C36B]"
          >
            <Sparkles className="w-4 h-4" /> {content.hero_overline || "Ecocomunidad campestre · Pextlán"}
          </span>

          <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1] tracking-tight">
            {content.hero_title_line_1 || "Cruza el portal hacia"}
            <span className="block italic gold-shimmer mt-2">{content.hero_title_line_2 || "tu cuento de hadas"}</span>
            <span className="block text-[#F2EFE6]/95 mt-2">{content.hero_title_line_3 || "entre montaña y arroyo."}</span>
          </h1>

          <p className="mt-7 max-w-xl text-[#F2EFE6]/80 text-base lg:text-lg leading-relaxed">
            {content.hero_description ||
              "14 lotes ecocampestres de 3,000 m² cada uno sobre 8 hectáreas de tierra virgen. A 7 minutos de Xico, 10 minutos de los Cañones de Pextlán. Sin vallas, solo barreras orgánicas."}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#preventa"
              data-testid="hero-cta-primary"
              className="inline-flex items-center rounded-full bg-gradient-to-r from-[#F1C36B] via-[#FFF7C2] to-[#B8E07A] text-[#0B1A0F] px-8 py-4 text-sm font-bold btn-lift shadow-[0_15px_45px_-10px_rgba(241,195,107,0.7)]"
            >
              {content.hero_cta_primary || "Ver preventa $1,000/m²"}
            </a>
            <a
              href="#lotes"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center rounded-full border border-[#F2EFE6]/40 text-[#F2EFE6] hover:bg-[#F2EFE6]/10 px-8 py-4 text-sm font-semibold btn-lift"
            >
              {content.hero_cta_secondary || "Recorrer la comunidad"}
            </a>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
            {[
              { k: content.hero_stat1_value || "8",  v: content.hero_stat1_label || "Hectáreas" },
              { k: content.hero_stat2_value || "14", v: content.hero_stat2_label || "Lotes ecocampestres" },
              { k: content.hero_stat3_value || "12", v: content.hero_stat3_label || "Amenidades" },
            ].map((s) => (
              <div key={s.v} data-testid={`hero-stat-${s.v}`}>
                <div className="font-serif text-3xl sm:text-4xl text-[#F1C36B]">
                  {s.k}
                </div>
                <div className="text-xs uppercase tracking-wider text-[#F2EFE6]/65 mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Portal frame */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7, rotateY: -30 }}
          animate={{ opacity: 1, scale: 1, rotateY: 0 }}
          transition={{ duration: 1.4, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="hidden lg:block lg:col-span-5"
          style={{ transformStyle: "preserve-3d" }}
        >
          <motion.div
            style={{ rotateY: tiltY, rotateX: tiltX, transformStyle: "preserve-3d" }}
            className="relative slow-tilt"
          >
            <div className="portal-frame portal-glow w-[420px] h-[560px] mx-auto relative">
              <img
                src={content.hero_portal_image || "https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&w=1200&q=85"}
                alt="Portal al bosque encantado"
                className="absolute inset-0 w-full h-full object-cover scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1A0F]/70 via-transparent to-[#15301E]/30" />
              <div className="absolute inset-x-0 bottom-0 p-7 text-white">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#F1C36B]">
                  {content.hero_portal_overline || "Bienvenido a"}
                </div>
                <div className="mt-1 font-serif text-3xl">
                  {content.hero_portal_title || "Pextlán"}
                </div>
                <div className="font-fairy text-2xl text-[#B8E07A] -mt-1">
                  {content.hero_portal_fairy || "donde el bosque te recibe"}
                </div>
              </div>
            </div>
            {/* Floating fairies orb */}
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#F1C36B]/40 blur-2xl animate-float-y" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-[#B8E07A]/40 blur-3xl" />
          </motion.div>
        </motion.div>
      </motion.div>

      <a
        href="#comunidad"
        aria-label="Bajar"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#F2EFE6]/80 animate-float-y z-10"
      >
        <ChevronDown className="w-6 h-6" />
      </a>
    </section>
  );
}
